import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Printer, MessageCircle, ArrowRight, CheckCircle, ArrowLeft, LayoutDashboard } from 'lucide-react';
import { useCompanySettings } from '../context/CompanySettingsContext';
import { useAuth } from '../context/AuthContext';
import { numberToWords } from '../utils/numberToWords';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import { NotificationContext } from '../context/NotificationContext';
import { whatsappAPI } from '../api/apiService';

export default function InvoiceSuccessPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { companySettings, refreshSettings } = useCompanySettings();
    const { user, userRole } = useAuth(); // Get current user and userRole

    // Refresh settings on mount
    React.useEffect(() => {
        refreshSettings();
    }, [refreshSettings]);

    const getInvoiceData = () => {
        if (location.state && Object.keys(location.state).length > 0) {
            const stateData = location.state;
            try {
                if (stateData.isPurchaseInvoice) {
                    localStorage.setItem("latest_invoice_type", "purchase");
                    localStorage.setItem("latest_purchase_request", JSON.stringify(stateData));
                } else {
                    localStorage.setItem("latest_invoice_type", "sales");
                    localStorage.setItem("latest_sales_invoice", JSON.stringify(stateData));
                }
            } catch (e) {}
            return stateData;
        }
        try {
            const type = localStorage.getItem("latest_invoice_type");
            if (type === "purchase") {
                const stored = localStorage.getItem("latest_purchase_request");
                if (stored) {
                    return JSON.parse(stored);
                }
            } else {
                const stored = localStorage.getItem("latest_sales_invoice");
                if (stored) {
                    return JSON.parse(stored);
                }
            }
        } catch (e) {}
        return {};
    };

    const [isPaused, setIsPaused] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendingStatus, setSendingStatus] = useState(''); // 'sending', 'success', 'error'
    const { addNotification } = React.useContext(NotificationContext);

    const invoice = getInvoiceData();

    const {
        totalAmount,
        subtotal: backendSubtotal,
        taxAmount,
        cgst,
        sgst,
        igst,
        taxRate,
        invoiceNo,
        items = [],
        customer,
        billingType,
        billingMode,
        billerName,
        billerId,
        created_by_referral_id,
        successDetails
    } = invoice;

    const handlePrint = () => {
        setIsPaused(true);
        window.print();
    };

    if (!invoiceNo) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
                <p className="text-gray-500 mb-4">No invoice data found.</p>
                <button 
                    onClick={() => navigate(invoice?.isPurchaseInvoice ? '/smart-purchase' : '/pos')} 
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg"
                >
                    {invoice?.isPurchaseInvoice ? "Go to Smart Purchase" : "Go to POS"}
                </button>
            </div>
        );
    }

    const handleWhatsApp = async () => {
        if (isSending) return;
        setIsSending(true);
        setSendingStatus('sending');
        
        try {
            const id = invoice.id || invoice.invoiceNo;
            if (!id) {
                throw new Error("Missing invoice ID for sending.");
            }
            
            await whatsappAPI.sendInvoice(id);
            setSendingStatus('success');
            addNotification("success", "WhatsApp Sent", "Invoice sent successfully.");
            
            setTimeout(() => {
                setIsSending(false);
                setSendingStatus('');
            }, 3000);
            
        } catch (error) {
            console.error("WhatsApp error:", error);
            setSendingStatus('error');
            const errorMsg = error.response?.data?.detail || "Failed to send invoice. Please try again.";
            addNotification("error", "WhatsApp Error", errorMsg);
            
            setTimeout(() => {
                setIsSending(false);
                setSendingStatus('');
            }, 3000);
        }
    };

    // Calculations
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const totalDiscount = items.reduce((sum, item) => sum + (item.discount || 0), 0);

    // Date & Time
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    const formattedTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    // Dynamic Title
    let headerTitle = "SALES INVOICE";
    if (invoice.isPurchaseInvoice) {
        headerTitle = "PURCHASE INVOICE";
    } else if (billingType?.includes('GST') || billingMode === 'with_gst') {
        headerTitle = "SALES INVOICE";
    } else if (billingType?.includes('Credit')) {
        headerTitle = "INVOICE (PENDING)";
    } else if (billingType?.includes('Non-GST') || billingMode === 'without_gst') {
        headerTitle = "ESTIMATE / NON-GST";
    }

    // Payment Logic
    const paymentMode = successDetails?.paymentMethod || "Cash"; // Default to Cash if missing
    const paidAmount = successDetails?.paidAmount !== undefined ? successDetails.paidAmount : totalAmount;
    // Calculate Change Due only for Cash if not provided
    const changeDue = successDetails?.balanceAmount !== undefined && successDetails?.balanceAmount < 0
        ? Math.abs(successDetails.balanceAmount)
        : (paymentMode === 'Cash' && paidAmount > totalAmount ? paidAmount - totalAmount : 0);
    const balanceDue = successDetails?.balanceAmount > 0 ? successDetails.balanceAmount : 0;

    return (
        <div className="min-h-screen bg-gray-100 p-6 print:p-0 print:bg-white">

            {/* Action Bar */}
            <div className="max-w-[210mm] mx-auto mb-6 flex items-center justify-between print:hidden">
                <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-bold">Generated Successfully</span>
                </div>
                <div className="flex gap-3">
                    <button 
                        onClick={() => navigate(invoice.isPurchaseInvoice ? '/smart-purchase' : '/pos')} 
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium whitespace-nowrap flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> {invoice.isPurchaseInvoice ? "Back to Smart Purchase" : "Back to POS Billing"}
                    </button>
                    <button 
                        onClick={() => {
                            if (user?.is_super_admin) {
                                navigate('/super-admin');
                            } else if (userRole === 'OWNER') {
                                navigate('/owner/dashboard');
                            } else if (userRole === 'SALES_EXECUTIVE') {
                                navigate('/sales-executive/dashboard');
                            } else {
                                navigate('/dashboard');
                            }
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium whitespace-nowrap flex items-center gap-2"
                    >
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </button>
                    <button 
                        onClick={handleWhatsApp} 
                        disabled={isSending}
                        className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 font-medium transition-all ${
                            sendingStatus === 'success' ? 'bg-green-600' :
                            sendingStatus === 'error' ? 'bg-red-600' :
                            'bg-[#25D366] hover:bg-[#128C7E]'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {isSending ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <MessageCircle className="w-4 h-4" /> 
                                <span>{sendingStatus === 'success' ? 'Sent!' : sendingStatus === 'error' ? 'Failed' : 'WhatsApp'}</span>
                            </>
                        )}
                    </button>
                    <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium">
                        <Printer className="w-4 h-4" /> Print
                    </button>
                </div>
            </div>

            {isSending && (
                <div className="max-w-[210mm] mx-auto mb-6 p-4 rounded-lg flex items-center justify-between text-sm font-semibold transition-all shadow-sm border animate-pulse bg-blue-50 border-blue-200 text-blue-800 print:hidden">
                    <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending invoice to WhatsApp...</span>
                    </div>
                </div>
            )}

            {sendingStatus === 'success' && (
                <div className="max-w-[210mm] mx-auto mb-6 p-4 rounded-lg flex items-center justify-between text-sm font-semibold transition-all shadow-sm border bg-green-50 border-green-200 text-green-800 print:hidden">
                    <span>Invoice sent successfully.</span>
                </div>
            )}

            {sendingStatus === 'error' && (
                <div className="max-w-[210mm] mx-auto mb-6 p-4 rounded-lg flex items-center justify-between text-sm font-semibold transition-all shadow-sm border bg-red-50 border-red-200 text-red-800 print:hidden">
                    <span>Failed to send invoice. Please try again.</span>
                </div>
            )}

            {/* Invoice A4 Container */}
            <div className="max-w-[210mm] mx-auto bg-white shadow-xl print:shadow-none p-8 print:p-0 text-sm" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>

                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                    <div className="flex gap-4">
                        {companySettings.logo && (
                            <img
                                src={resolveMediaUrl(companySettings.logo)}
                                alt="Company Logo"
                                className="w-40 h-40 object-contain rounded"
                                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                            />
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 tracking-tight mb-1 uppercase">
                                {companySettings.name || "Your Company Name"}
                            </h1>
                            <p className="text-gray-500 w-64 leading-relaxed text-xs">
                                {companySettings.street_address && <>{companySettings.street_address}<br /></>}
                                {companySettings.city && <>{companySettings.city}, {companySettings.state} {companySettings.postal_code}<br /></>}
                                {companySettings.phone && <>Phone: {companySettings.phone}<br /></>}
                                {companySettings.email && <>Email: {companySettings.email}</>}
                                {(companySettings.tax_id || companySettings.gstin || (invoice?.company_details?.tax_id) || (invoice?.company_details?.gstin)) && (
                                    <><br />GSTIN: {companySettings.tax_id || companySettings.gstin || invoice?.company_details?.tax_id || invoice?.company_details?.gstin}</>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <h2 className="text-3xl font-bold text-blue-800 uppercase tracking-wider mb-2">
                            {headerTitle}
                        </h2>
                        <div className="bg-blue-50 p-3 rounded border border-blue-100 inline-block text-left min-w-[220px]">
                            <div className="flex justify-between mb-1">
                                <span className="font-bold text-gray-600">Invoice #:</span>
                                <span className="font-bold">{invoiceNo}</span>
                            </div>
                            <div className="flex justify-between mb-1">
                                <span className="font-bold text-gray-600">Date:</span>
                                <span>{formattedDate}</span>
                            </div>
                            <div className="flex justify-between mb-1">
                                <span className="font-bold text-gray-600">Time:</span>
                                <span>{formattedTime}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-bold text-gray-600">Status:</span>
                                {billingType === 'Credit' ? (
                                    <span className="text-orange-600 font-bold uppercase">PENDING</span>
                                ) : (
                                    <span className="text-green-600 font-bold uppercase">Paid</span>
                                )}
                            </div>
                            <div className="flex justify-between mt-1 pt-1 border-t border-blue-200">
                                <span className="font-bold text-gray-500 text-[10px] uppercase">Billed By:</span>
                                <span className="text-xs font-medium">
                                    {(billerName && billerId) ? `${billerName} (${billerId})` : (user?.salesman_id ? `${user.name} (${user.salesman_id})` : "Staff")}
                                </span>
                            </div>
                            <div className="flex justify-between mt-1 pt-1 border-t border-blue-200">
                                <span className="font-bold text-gray-500 text-[10px] uppercase">Referral ID:</span>
                                <span className="text-xs font-medium uppercase">
                                    {created_by_referral_id || user?.referral_id || "None"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bill To Section */}
                <div className="mb-8">
                    <div className="bg-blue-800 text-white px-4 py-1 font-bold uppercase tracking-wider mb-2">
                        {invoice.isPurchaseInvoice ? "Purchase Request (Customer)" : "Bill To (Customer)"}
                    </div>
                    {billingType === 'Walk-in' || !customer ? (
                        <div className="px-2 text-gray-700 font-bold">
                            Walk-in Customer
                        </div>
                    ) : (
                        <div className="px-2">
                            <h3 className="font-bold text-lg">{customer.name}</h3>
                            <p className="text-gray-600">Phone: {customer.phone}</p>
                            {(billingType?.includes('GST') || billingType === 'Customer' || billingType === 'Credit' || billingMode === 'with_gst') && customer?.address && (
                                <p className="text-gray-600">Address: {customer.address}</p>
                            )}
                            {(billingType?.includes('GST') || billingMode === 'with_gst') && customer?.gstin && (
                                <p className="text-gray-600 font-bold">GSTIN: {customer.gstin}</p>
                            )}
                            {billingType === 'Credit' && (
                                <p className="text-red-500 text-xs font-bold mt-1">Pending Balance: ₹{balanceDue.toFixed(2)}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Items Table */}
                <div className="mb-6 p-1">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-blue-800 text-white text-[10px] uppercase tracking-wider">
                                <th className="p-2 text-center border-r border-blue-700 w-8">#</th>
                                <th className="p-2 text-left border-r border-blue-700">Item Description</th>
                                <th className="p-2 text-center border-r border-blue-700 w-16">HSN</th>
                                <th className="p-2 text-center border-r border-blue-700 w-12">Qty</th>
                                <th className="p-2 text-right border-r border-blue-700 w-20">Rate</th>
                                <th className="p-2 text-right border-r border-blue-700 w-16">Disc</th>
                                {(billingType?.includes('GST') || billingMode === 'with_gst') && <th className="p-2 text-center border-r border-blue-700 w-14">Tax %</th>}
                                <th className="p-2 text-right w-20">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-700 text-xs">
                            {items.map((item, index) => (
                                <tr key={index} className="border-b border-gray-200">
                                    <td className="p-2 text-center border-r border-gray-200">{index + 1}</td>
                                    <td className="p-2 text-left border-r border-gray-200">
                                        <div className="font-bold">
                                            {item.name} 
                                            {item.variant_name && (
                                                <span className="text-blue-600 font-medium ml-1">
                                                    ({item.variant_name})
                                                </span>
                                            )}
                                            {item.is_gift && (
                                                <span className="ml-2 bg-purple-100 text-purple-700 px-1 py-0.5 rounded text-[8px] uppercase font-black">
                                                    Gift
                                                </span>
                                            )}
                                        </div>
                                        {item.asn_code && (
                                            <div className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                                ASN: {item.asn_code}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-2 text-center border-r border-gray-200">{item.hsn || "-"}</td>
                                    <td className="p-2 text-center border-r border-gray-200">
                                        {parseFloat(item.qty)} {item.unit ? <span className="text-[9px] text-gray-500 font-medium ml-0.5 uppercase">{item.unit}</span> : (item.product_type === 'weight_based' ? <span className="text-[9px] text-gray-500 font-medium ml-0.5 uppercase">KG</span> : '')}
                                    </td>
                                    <td className="p-2 text-right border-r border-gray-200">₹{parseFloat(item.price).toLocaleString()}</td>
                                    <td className="p-2 text-right border-r border-gray-200 font-medium text-green-600">
                                        {item.discount > 0 ? `-₹${item.discount}` : "-"}
                                    </td>
                                    {(billingType?.includes('GST') || billingMode === 'with_gst') && <td className="p-2 text-center border-r border-gray-200">
                                        {item.tax_rate || item.tax || 0}%
                                    </td>}
                                    <td className="p-2 text-right font-bold">₹{((item.price * item.qty) - (item.discount || 0)).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* GST Summary Table (Grouped by Rate) */}
                {(billingType?.includes('GST') || billingMode === 'with_gst') && (
                    <div className="mb-8 p-1">
                        <div className="bg-gray-100 px-3 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-widest border border-gray-200 border-b-0 rounded-t w-fit">
                            GST Tax Summary
                        </div>
                        <table className="w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-50 text-[10px] font-black uppercase text-gray-700 border-b border-gray-300">
                                    <th className="p-2 text-left border-r border-gray-300">GST %</th>
                                    <th className="p-2 text-right border-r border-gray-300">Taxable Value</th>
                                    <th className="p-2 text-center border-r border-gray-300">CGST %</th>
                                    <th className="p-2 text-right border-r border-gray-300">CGST Amount</th>
                                    <th className="p-2 text-center border-r border-gray-300">SGST %</th>
                                    <th className="p-2 text-right">SGST Amount</th>
                                </tr>
                            </thead>
                            <tbody className="text-[10px] font-medium text-gray-600">
                                {(function() {
                                    // Calculate summary if not provided by backend/state
                                    const summary = invoice.gst_summary || (items.reduce((acc, item) => {
                                        const rate = parseFloat(item.tax_rate || item.tax || 0);
                                        const taxable = (item.price * item.qty) - (item.discount || 0);
                                        const gstAmt = (taxable * rate) / 100;
                                        
                                        if (!acc[rate]) {
                                            acc[rate] = { gst_rate: rate, taxable_value: 0, cgst_amount: 0, sgst_amount: 0 };
                                        }
                                        acc[rate].taxable_value += taxable;
                                        acc[rate].cgst_amount += gstAmt / 2;
                                        acc[rate].sgst_amount += gstAmt / 2;
                                        return acc;
                                    }, {}));
                                    
                                    const slabs = Array.isArray(summary) ? summary : Object.values(summary).sort((a,b) => a.gst_rate - b.gst_rate);
                                    
                                    return slabs.map((slab, idx) => (
                                        <tr key={idx} className="border-b border-gray-200">
                                            <td className="p-2 text-left border-r border-gray-300 font-bold">{slab.gst_rate}%</td>
                                            <td className="p-2 text-right border-r border-gray-300">₹{slab.taxable_value.toFixed(2)}</td>
                                            <td className="p-2 text-center border-r border-gray-300">{slab.gst_rate / 2}%</td>
                                            <td className="p-2 text-right border-r border-gray-300">₹{slab.cgst_amount.toFixed(2)}</td>
                                            <td className="p-2 text-center border-r border-gray-300">{slab.gst_rate / 2}%</td>
                                            <td className="p-2 text-right">₹{slab.sgst_amount.toFixed(2)}</td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Footer Section */}
                <div className="flex gap-8 items-start">
                    {/* QR Code Section */}
                    <div className="flex-1">
                        <div className="bg-white border-2 border-dashed border-blue-200 p-6 rounded-2xl h-full flex flex-col items-center justify-center text-center">
                            <div className="mb-4 p-3 bg-blue-50 rounded-2xl border border-blue-100 shadow-sm">
                                <img 
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/public/invoice/' + invoiceNo)}`} 
                                    alt="Invoice QR"
                                    className="w-32 h-32"
                                />
                            </div>
                            <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wider mb-1">Scan for Digital Copy</h4>
                            <p className="text-[10px] text-gray-400 font-medium px-4">
                                Point your camera here to view, download, or share this invoice digitally.
                            </p>
                        </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="w-80">
                        <div className="bg-blue-800 text-white px-4 py-1 font-bold uppercase tracking-wider text-center text-sm">
                            Financial Summary
                        </div>
                        <div className="border border-t-0 border-gray-300 bg-gray-50 p-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="font-bold text-gray-600">Subtotal:</span>
                                <span className="font-bold text-gray-800">₹{subtotal.toLocaleString()}</span>
                            </div>
                            {totalDiscount > 0 && (
                                <div className="flex justify-between text-sm text-green-600 bg-green-50 p-1 -mx-1 rounded">
                                    <span className="font-bold underline underline-offset-2">Total Savings:</span>
                                    <span className="font-bold">-₹{totalDiscount.toLocaleString()}</span>
                                </div>
                            )}

                            {billingMode === 'with_gst' && (
                                <>
                                    {cgst > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>CGST:</span>
                                            <span>₹{cgst.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {sgst > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>SGST:</span>
                                            <span>₹{sgst.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {igst > 0 && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>IGST ({taxRate}%):</span>
                                            <span>₹{igst.toFixed(2)}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="border-t border-gray-300 my-2"></div>

                            <div className="flex justify-between text-lg font-extrabold bg-blue-100 p-2 -mx-4 -mb-4 border-t border-blue-200">
                                <span className="text-blue-900">GRAND TOTAL:</span>
                                <span className="text-blue-900">₹{totalAmount.toLocaleString()}</span>
                            </div>

                            <div className="mt-6 text-right pb-2 border-b border-gray-100">
                                <p className="text-[10px] uppercase font-bold text-gray-500">Amount in Words</p>
                                <p className="text-xs font-bold text-gray-700 italic">{function () {
                                    try { return numberToWords(totalAmount); } catch (e) { return ""; }
                                }()}</p>
                            </div>

                            {/* Payment Details Section */}
                            <div className="mt-6 pt-2 border-t border-dashed border-gray-300">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600 font-bold">Payment Mode:</span>
                                    <span className="font-bold">{paymentMode}</span>
                                </div>
                                {successDetails?.cardRefNo && (
                                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                                        <span className="font-bold">Ref No:</span>
                                        <span className="font-mono">{successDetails.cardRefNo}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm text-green-700 font-bold mb-1">
                                    <span>Paid Amount:</span>
                                    <span>₹{parseFloat(paidAmount).toLocaleString()}</span>
                                </div>
                                {changeDue > 0 && (
                                    <div className="flex justify-between text-xs text-gray-500 font-bold mb-1">
                                        <span>Change Return:</span>
                                        <span>₹{parseFloat(changeDue).toLocaleString()}</span>
                                    </div>
                                )}
                                {balanceDue > 0 && (
                                    <div className="flex justify-between text-sm text-red-600 font-bold bg-red-50 p-1 rounded mt-1">
                                        <span>Balance Due:</span>
                                        <span>₹{parseFloat(balanceDue).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Footer Message */}
                <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                    <p className="text-sm font-bold text-blue-800 tracking-wide uppercase italic">
                        Thank you for purchasing with {companySettings.name || "Your Company Name"}
                    </p>
                </div>
            </div>
        </div>
    );
}
